import * as THREE from 'three';
import { box, cylinderN, mergeParts, part, taperedCylinder, type Part } from '@/art/geometry';
import { emissive, vertexColored } from '@/art/materials';

/**
 * Straatlantaarn — het belangrijkste prop van de wijk.
 *
 * De verlichting van New Amsterdam is oranje en onvolledig: lantaarns die het half
 * begeven, warme plassen licht met donker ertussen. Deze lamp levert drie dingen: een
 * verticaal silhouet, een emissieve kap die altijd zichtbaar is, en een aanhechtpunt voor
 * een echte lichtbron uit de pool.
 *
 * Origin: onderkant van de voet, op (0, 0, 0).
 */

export const STREETLAMP = {
  totalHeight: 4.4,
  baseHeight: 0.35,
  baseRadius: 0.19,
  poleRadius: 0.075,
  armLength: 0.85,
  headWidth: 0.34,
  headHeight: 0.3,
  sides: 16,
  /** Hoogte waarop de lichtbron hangt — gebruikt door de LightPool. */
  lightHeight: 4.05,
  triBudget: 320,
} as const;

export function buildStreetlamp(): THREE.Group {
  const group = new THREE.Group();
  group.name = 'NA_Prop_Streetlamp';

  const poleHeight = STREETLAMP.totalHeight - STREETLAMP.baseHeight;

  const solid: Part[] = [
    // Zware voet: op speelafstand leest een dunne paal als een streep, de voet geeft hem gewicht.
    part(
      taperedCylinder(STREETLAMP.poleRadius * 1.6, STREETLAMP.baseRadius, STREETLAMP.baseHeight, STREETLAMP.sides)
        .translate(0, STREETLAMP.baseHeight / 2, 0),
      'darkSteel',
    ),
    part(cylinderN(STREETLAMP.baseRadius * 1.12, 0.08, STREETLAMP.sides).translate(0, 0.04, 0), 'nightStone'),
    part(cylinderN(STREETLAMP.baseRadius * 0.92, 0.07, STREETLAMP.sides).translate(0, STREETLAMP.baseHeight + 0.035, 0), 'slateGrey'),
    part(
      cylinderN(STREETLAMP.poleRadius, poleHeight, STREETLAMP.sides, true).translate(
        0,
        STREETLAMP.baseHeight + poleHeight / 2,
        0,
      ),
      'darkSteel',
    ),
    // Arm die naar de straat uitsteekt, met de kap eraan.
    part(
      box(STREETLAMP.armLength, 0.07, 0.07).translate(
        STREETLAMP.armLength / 2,
        STREETLAMP.totalHeight - 0.12,
        0,
      ),
      'darkSteel',
    ),
    part(
      box(0.08, 0.3, 0.08).translate(
        STREETLAMP.armLength * 0.42,
        STREETLAMP.totalHeight - 0.24,
        0,
      ),
      'darkSteel',
    ),
    part(
      taperedCylinder(STREETLAMP.headWidth * 0.35, STREETLAMP.headWidth / 2, STREETLAMP.headHeight, STREETLAMP.sides)
        .translate(STREETLAMP.armLength, STREETLAMP.lightHeight + STREETLAMP.headHeight / 2, 0),
      'darkSteel',
    ),
  ];

  const shell = new THREE.Mesh(mergeParts(solid), vertexColored());
  shell.name = 'shell';
  shell.castShadow = true;
  group.add(shell);

  // Het lichtgevende vlak onder de kap. Dit blijft ook zichtbaar als de puntlichtbron door
  // de pool is uitgeschakeld — de lamp gaat dus nooit "uit" in beeld.
  const lens = new THREE.Mesh(
    cylinderN(STREETLAMP.headWidth * 0.42, 0.06, STREETLAMP.sides).translate(
      STREETLAMP.armLength,
      STREETLAMP.lightHeight - 0.02,
      0,
    ),
    emissive('lampGlow', 1),
  );
  lens.name = 'lens';
  group.add(lens);

  return group;
}

/** Waar de lichtbron van deze lamp hangt, in de lokale ruimte van het prop. */
export function streetlampLightOffset(): THREE.Vector3 {
  return new THREE.Vector3(STREETLAMP.armLength, STREETLAMP.lightHeight - 0.1, 0);
}
